import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

interface PlanBadgeProps {
  plan: 'free' | 'hs60' | 'hs90' | 'hs130' | 'hs240';
  size?: 'small' | 'medium' | 'large';
}

const getBadgeBg = (plan: string): string => {
  switch (plan) {
    case 'free': return '#363636';
    case 'hs60': return '#ffc778';
    case 'hs90': return '#ffac37';
    case 'hs130': return '#ff9601';
    case 'hs240': return '#363636';
    default: return '#363636';
  }
};

const getBadgeColor = (plan: string): string => {
  switch (plan) {
    case 'free': return '#ffffff';
    case 'hs60': return '#ffffff';
    case 'hs90': return '#ffffff';
    case 'hs130': return '#ffffff';
    case 'hs240': return '#ff9601';
    default: return '#ffffff';
  }
};

const Badge = styled.span<{ $plan: string; $size: string; $clickable: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${props => 
    props.$size === 'small' ? '0.15rem 0.4rem' :
    props.$size === 'large' ? '0.5rem 1rem' :
    '0.35rem 0.75rem'
  };
  border-radius: var(--radius-full);
  font-size: ${props => 
    props.$size === 'small' ? '0.55rem' :
    props.$size === 'large' ? '0.95rem' :
    '0.7rem'
  };
  font-weight: 700;
  text-transform: uppercase;
  font-family: 'Poppins', sans-serif;
  letter-spacing: 0.05em;
  color: ${props => getBadgeColor(props.$plan)};
  background: ${props => getBadgeBg(props.$plan)};
  box-shadow: var(--shadow-sm);
  white-space: nowrap;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: all 0.2s ease;
  
  ${props => props.$clickable && `
    &:hover {
      transform: scale(1.05);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }
    
    &:active {
      transform: scale(0.98);
    }
  `}
`;

const PlanBadge: React.FC<PlanBadgeProps> = ({ plan, size = 'medium' }) => {
  const navigate = useNavigate();
  
  const handleClick = () => {
    navigate('/pricing');
  };
  
  // Map plan keys to display names
  const getDisplayName = (planKey: string): string => {
    switch (planKey) {
      case 'free':
        return 'FREE';
      case 'hs60':
        return 'HS60';
      case 'hs90':
        return 'HS90';
      case 'hs130':
        return 'HS130';
      case 'hs240':
        return 'HS240';
      default:
        return planKey.toUpperCase();
    }
  };
  
  return (
    <Badge $plan={plan} $size={size} $clickable={true} onClick={handleClick}>
      {getDisplayName(plan)}
    </Badge>
  );
};

export default PlanBadge;

